import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { Search, Mail, Send, Check, X, RefreshCw, User, DollarSign, AlertTriangle } from 'lucide-react';
import { colors, fontFamily, optionStyle, selectSurface } from '../theme';

const gold = colors.textPrimary;
const muted = colors.textMuted;
const accent = colors.gold;
const card = { background: colors.cardBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, padding: '1.25rem', marginBottom: '1.25rem', boxShadow: colors.shadowCard };
const inputStyle = { padding: '0.6rem 0.75rem', background: colors.surfaceBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 4, color: colors.textPrimary, fontFamily, fontSize: '0.85rem' };
const btnPrimary = { padding: '0.5rem 1rem', background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 4, cursor: 'pointer', fontFamily, display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' };
const btnCancel = { padding: '0.5rem 1rem', background: 'rgba(255,255,255,0.1)', border: 'none', color: colors.textPrimary, borderRadius: 4, cursor: 'pointer', fontFamily, fontSize: '0.85rem' };
const btnDanger = { padding: '0.5rem 1rem', background: `${colors.negative}33`, border: `1px solid ${colors.negative}`, color: colors.negative, borderRadius: 4, cursor: 'pointer', fontFamily, fontSize: '0.85rem' };
const btnGreen = { padding: '0.5rem 1rem', background: `${colors.odds}33`, border: `1px solid ${colors.odds}`, color: colors.odds, borderRadius: 4, cursor: 'pointer', fontFamily, fontSize: '0.85rem' };
const modalOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalBox = { background: colors.bgGradient, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, padding: '1.5rem', width: '100%', maxWidth: 720, maxHeight: '90vh', overflowY: 'auto' };

const statusColors = {
  pending: { bg: `${colors.pending}26`, color: colors.pending },
  won: { bg: `${colors.odds}26`, color: colors.odds },
  lost: { bg: `${colors.negative}26`, color: colors.negative },
  void: { bg: 'rgba(108,117,125,0.15)', color: colors.textFaint },
};
const StatusBadge = ({ status }) => {
  const c = statusColors[status] || statusColors.void;
  return <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 20, background: c.bg, color: c.color }}>{status?.toUpperCase()}</span>;
};

export default function CustomerServiceView({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('messages');
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    document.title = 'Support | CakeStakes';
  }, []);

  useEffect(() => {
    checkUnread();
    const t = setInterval(checkUnread, 30000);
    return () => clearInterval(t);
  }, []);

  const checkUnread = async () => {
    try {
      const data = await api.getUnreadCount();
      setUnreadCount(data.unread_count || 0);
    } catch {}
  };

  const tabs = [
    { id: 'messages', label: 'Messages' },
    { id: 'customers', label: 'Customer Lookup' },
    { id: 'rules', label: 'Rules & FAQ' },
    { id: 'fraud', label: 'Live Fraud' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: colors.bgGradient, fontFamily, color: gold }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1.5rem', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(12px)', borderBottom: `1px solid ${colors.goldMuted}` }}>
        <h1 style={{ margin: 0, color: gold, fontSize: '1.2rem', fontWeight: 700 }}>Customer Support</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ color: muted, fontSize: '0.9rem' }}>Hi, <strong style={{ color: gold }}>{user.name}</strong></span>
          <button onClick={onLogout} style={{ background: colors.cardBg, border: 'none', color: colors.textPrimary, borderRadius: 4, padding: '0.4rem 1rem', cursor: 'pointer', fontFamily, fontWeight: 600, fontSize: '0.85rem' }}>
            Logout
          </button>
        </div>
      </header>

      <nav style={{ display: 'flex', background: colors.cardBg, borderBottom: `1px solid ${colors.goldMuted}`, padding: '0 0.75rem' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '0.85rem 1.2rem', background: 'none', border: 'none',
              color: activeTab === tab.id ? gold : muted, cursor: 'pointer',
              borderBottom: activeTab === tab.id ? `3px solid ${accent}` : '3px solid transparent',
              fontFamily, fontSize: '0.9rem',
              fontWeight: activeTab === tab.id ? 700 : 400,
              display: 'flex', alignItems: 'center', gap: '0.4rem'
            }}
          >
            {tab.label}
            {tab.id === 'messages' && unreadCount > 0 && (
              <span style={{ background: colors.negative, color: '#fff', borderRadius: 20, padding: '1px 7px', fontSize: '0.72rem' }}>{unreadCount}</span>
            )}
          </button>
        ))}
      </nav>

      <main style={{ padding: '1.5rem', maxWidth: 1200, margin: '0 auto' }}>
        {activeTab === 'messages' && <CSMessages onUnreadChange={checkUnread} />}
        {activeTab === 'customers' && <CustomerLookup />}
        {activeTab === 'rules' && <CSRulesView />}
        {activeTab === 'fraud' && <CSFraudView />}
      </main>
    </div>
  );
}

// ─── Messages Panel ───────────────────────────────────────────────────────────
function CSMessages({ onUnreadChange }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => { loadMessages(); }, []);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await api.getInbox();
      setMessages(data.messages || []);
    } catch {}
    finally { setLoading(false); }
  };

  const openMessage = async (msg) => {
    try {
      const data = await api.getMessage(msg.id);
      setSelected(data.message);
      setReplies(data.replies || []);
      if (!msg.is_read) { await api.markMessageRead(msg.id); loadMessages(); onUnreadChange(); }
    } catch {}
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selected) return;
    try {
      await api.replyToMessage(selected.id, replyText);
      setReplyText('');
      openMessage(selected);
    } catch (e) { alert(e.response?.data?.error || 'Failed to send'); }
  };

  const resolve = async (id) => {
    try {
      await api.resolveMessage(id);
      loadMessages();
      if (selected?.id === id) setSelected(s => ({ ...s, is_resolved: true }));
    } catch {}
  };

  const unresolve = async (id) => {
    try {
      await api.unresolveMessage(id);
      loadMessages();
      if (selected?.id === id) setSelected(s => ({ ...s, is_resolved: false }));
    } catch {}
  };

  const filtered = messages.filter(m => {
    if (filter === 'unresolved') return !m.is_resolved;
    if (filter === 'resolved') return m.is_resolved;
    return true;
  });
  const unresolved = messages.filter(m => !m.is_resolved).length;

  const filterBtn = (val, label, color) => (
    <button onClick={() => setFilter(val)} style={{ padding: '0.28rem 0.7rem', fontSize: '0.75rem', borderRadius: 20, border: 'none', cursor: 'pointer', fontFamily, background: filter === val ? color : 'rgba(255,255,255,0.08)', color: filter === val ? '#fff' : muted }}>
      {label}
    </button>
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
      {/* List */}
      <div style={{ background: colors.cardBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, overflow: 'hidden', boxShadow: colors.shadowCard }}>
        <div style={{ padding: '0.75rem 1rem', borderBottom: `1px solid ${colors.goldMuted}`, background: colors.sectionBg, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: gold, fontWeight: 600, fontSize: '0.9rem' }}>Inbox</span>
          <div style={{ display: 'flex', gap: 6 }}>
            {filterBtn('all', `All (${messages.length})`, accent)}
            {filterBtn('unresolved', `Open (${unresolved})`, colors.negative)}
            {filterBtn('resolved', `Resolved (${messages.length - unresolved})`, colors.odds)}
          </div>
        </div>
        <div style={{ maxHeight: 540, overflowY: 'auto' }}>
          {loading ? <div style={{ padding: '2rem', textAlign: 'center', color: muted }}>Loading...</div>
            : filtered.length === 0 ? <div style={{ padding: '2rem', textAlign: 'center', color: muted }}>No messages</div>
            : filtered.map(msg => (
              <div key={msg.id} onClick={() => openMessage(msg)} style={{ padding: '0.75rem 1rem', borderBottom: '1px solid rgba(191,149,63,0.08)', cursor: 'pointer', background: selected?.id === msg.id ? colors.goldActiveBg : !msg.is_read ? 'rgba(100,120,255,0.07)' : 'transparent' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.15rem' }}>
                  <span style={{ color: !msg.is_read ? gold : muted, fontWeight: !msg.is_read ? 700 : 400, fontSize: '0.84rem' }}>{msg.sender_name || 'Unknown'}</span>
                  <span style={{ color: muted, fontSize: '0.72rem' }}>{new Date(msg.created_at).toLocaleDateString()}</span>
                </div>
                <div style={{ color: gold, fontSize: '0.82rem', marginBottom: '0.12rem', fontWeight: 500 }}>{msg.subject}</div>
                <div style={{ color: muted, fontSize: '0.77rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{msg.message}</div>
                <div style={{ display: 'flex', gap: 5, marginTop: '0.3rem' }}>
                  {!msg.is_read && <span style={{ fontSize: '0.69rem', padding: '1px 7px', borderRadius: 20, background: 'rgba(100,120,255,0.2)', color: colors.freePlay }}>New</span>}
                  {msg.is_resolved && <span style={{ fontSize: '0.69rem', padding: '1px 7px', borderRadius: 20, background: `${colors.odds}26`, color: colors.odds }}>Resolved</span>}
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Detail */}
      <div style={{ background: colors.cardBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: colors.shadowCard }}>
        {selected ? (
          <>
            <div style={{ padding: '0.75rem 1rem', borderBottom: `1px solid ${colors.goldMuted}`, background: colors.sectionBg, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ color: gold, fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.2rem' }}>{selected.subject}</div>
                <div style={{ color: muted, fontSize: '0.77rem' }}>From: {selected.sender_name} · {new Date(selected.created_at).toLocaleString()}</div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {selected.is_resolved
                  ? <button onClick={() => unresolve(selected.id)} style={{ ...btnPrimary, background: 'rgba(108,117,125,0.2)', border: '1px solid rgba(108,117,125,0.5)', color: '#adb5bd', fontSize: '0.8rem' }}>Reopen</button>
                  : <button onClick={() => resolve(selected.id)} style={btnGreen}><Check size={13} /> Resolve</button>
                }
                <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: muted, cursor: 'pointer' }}><X size={18} /></button>
              </div>
            </div>
            <div style={{ padding: '1rem', overflowY: 'auto', flex: 1, maxHeight: 320 }}>
              <div style={{ background: colors.sectionBg, borderRadius: 6, padding: '0.75rem', marginBottom: '1rem' }}>
                <p style={{ color: gold, margin: 0, fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>{selected.message}</p>
              </div>
              {replies.map(r => (
                <div key={r.id} style={{ background: 'rgba(100,120,255,0.08)', border: '1px solid rgba(100,120,255,0.2)', borderRadius: 6, padding: '0.65rem 0.75rem', marginBottom: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
                    <span style={{ color: colors.freePlay, fontSize: '0.77rem' }}>{r.sender_name}</span>
                    <span style={{ color: muted, fontSize: '0.72rem' }}>{new Date(r.created_at).toLocaleString()}</span>
                  </div>
                  <p style={{ color: gold, margin: 0, fontSize: '0.83rem', whiteSpace: 'pre-wrap' }}>{r.message}</p>
                </div>
              ))}
            </div>
            <div style={{ padding: '0.75rem 1rem', borderTop: `1px solid ${colors.goldMuted}`, display: 'flex', gap: '0.5rem' }}>
              <textarea value={replyText} onChange={e => setReplyText(e.target.value)} rows={2} placeholder="Type reply..." style={{ ...inputStyle, flex: 1, width: '100%', resize: 'none' }} />
              <button onClick={sendReply} disabled={!replyText.trim()} style={{ ...btnPrimary, alignSelf: 'stretch', padding: '0 1rem', opacity: !replyText.trim() ? 0.5 : 1 }}><Send size={14} /></button>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, padding: '3rem', color: muted, minHeight: 200 }}>
            <Mail size={32} style={{ marginBottom: '0.75rem', opacity: 0.4 }} />
            <p style={{ margin: 0, fontSize: '0.88rem' }}>Select a message to view</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Customer Lookup ──────────────────────────────────────────────────────────
function CustomerLookup() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const [actionErr, setActionErr] = useState(null);
  const [activeSection, setActiveSection] = useState('bets');

  // Modals
  const [showSettleModal, setShowSettleModal] = useState(null); // ticket_number
  const [settleOutcome, setSettleOutcome] = useState('won');
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustType, setAdjustType] = useState('credit');
  const [adjustReason, setAdjustReason] = useState('');

  const search = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    try {
      const data = await api.cs.searchCustomers(query);
      setResults(data.customers || []);
    } catch (err) {
      setResults([]);
    } finally { setSearching(false); }
  };

  const loadDetail = async (customer) => {
    setSelectedCustomer(customer);
    setDetail(null);
    setLoadingDetail(true);
    setActionMsg(null);
    setActionErr(null);
    setActiveSection('bets');
    try {
      const data = await api.cs.getCustomer(customer.id);
      setDetail(data);
    } catch {}
    finally { setLoadingDetail(false); }
  };

  const voidTicket = async (ticketNumber) => {
    if (!window.confirm(`Void ticket ${ticketNumber} and refund the stake?`)) return;
    try {
      const data = await api.cs.voidTicket(ticketNumber);
      setActionMsg(data.message);
      loadDetail(selectedCustomer);
    } catch (err) { setActionErr(err.response?.data?.error || 'Failed to void'); }
  };

  const settleTicket = async () => {
    try {
      const data = await api.cs.settleTicket(showSettleModal, settleOutcome);
      setActionMsg(data.message);
      setShowSettleModal(null);
      loadDetail(selectedCustomer);
    } catch (err) { setActionErr(err.response?.data?.error || 'Failed to settle'); }
  };

  const submitAdjust = async () => {
    const amt = parseFloat(adjustAmount);
    if (!amt) { setActionErr('Enter a valid amount'); return; }
    try {
      const data = await api.cs.adjustBalance(selectedCustomer.id, amt, adjustType, adjustReason || 'CS adjustment');
      setActionMsg(data.message);
      setShowAdjustModal(false);
      setAdjustAmount(''); setAdjustReason('');
      loadDetail(selectedCustomer);
    } catch (err) { setActionErr(err.response?.data?.error || 'Adjustment failed'); }
  };

  const detailLabel = { color: muted, fontSize: '0.77rem', display: 'block', marginBottom: '0.15rem' };
  const detailVal = { color: gold, fontWeight: 600, fontSize: '0.87rem', margin: 0 };

  return (
    <div>
      {/* Search */}
      <div style={card}>
        <form onSubmit={search} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <label style={{ display: 'block', color: muted, fontSize: '0.82rem', marginBottom: '0.3rem' }}>Search Customer</label>
            <input
              type="text" value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Name or username..."
              style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', paddingLeft: '2rem' }}
            />
            <Search size={14} style={{ position: 'absolute', left: 8, bottom: 10, color: muted }} />
          </div>
          <button type="submit" disabled={searching} style={{ ...btnPrimary, opacity: searching ? 0.6 : 1 }}>
            <Search size={14} /> {searching ? 'Searching...' : 'Search'}
          </button>
        </form>

        {results.length > 0 && (
          <div style={{ marginTop: '1rem', border: `1px solid ${colors.goldMuted}`, borderRadius: 6, overflow: 'hidden' }}>
            {results.map((c, i) => (
              <div key={c.id} onClick={() => loadDetail(c)} style={{ padding: '0.6rem 0.85rem', borderBottom: i < results.length - 1 ? '1px solid rgba(191,149,63,0.08)' : 'none', cursor: 'pointer', background: selectedCustomer?.id === c.id ? colors.goldActiveBg : colors.sectionBg, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ color: gold, fontWeight: 600, fontSize: '0.85rem' }}>{c.name}</span>
                  <span style={{ color: muted, fontSize: '0.78rem', marginLeft: 8 }}>@{c.username}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ color: muted, fontSize: '0.77rem' }}>{c.parent_name}</span>
                  {!c.is_active && <span style={{ fontSize: '0.7rem', padding: '1px 7px', borderRadius: 20, background: `${colors.negative}26`, color: colors.negative }}>Inactive</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail */}
      {selectedCustomer && (
        <div>
          {loadingDetail ? (
            <div style={{ ...card, textAlign: 'center', color: muted }}>Loading...</div>
          ) : detail ? (
            <div>
              {actionMsg && <div style={{ background: `${colors.odds}1a`, border: `1px solid ${colors.odds}`, color: colors.odds, borderRadius: 6, padding: '0.65rem 0.85rem', marginBottom: '0.75rem', fontSize: '0.85rem' }}>{actionMsg}</div>}
              {actionErr && <div style={{ background: `${colors.negative}1a`, border: `1px solid ${colors.negative}`, color: colors.negative, borderRadius: 6, padding: '0.65rem 0.85rem', marginBottom: '0.75rem', fontSize: '0.85rem' }}>{actionErr}</div>}

              {/* Customer info */}
              <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <User size={18} style={{ color: accent }} />
                    <h3 style={{ color: gold, margin: 0, fontSize: '1.05rem' }}>{detail.customer.name}</h3>
                    <span style={{ color: muted, fontSize: '0.82rem' }}>@{detail.customer.username}</span>
                    {!detail.customer.is_active && <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 20, background: `${colors.negative}26`, color: colors.negative }}>Inactive</span>}
                  </div>
                  <button onClick={() => setShowAdjustModal(true)} style={btnPrimary}><DollarSign size={14} /> Adjust Balance</button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
                  {[
                    { label: 'Book', val: detail.customer.book_name },
                    { label: 'Parent', val: `${detail.customer.parent_name} (${detail.customer.parent_role})` },
                    { label: 'Credit Limit', val: `$${detail.customer.weekly_limit.toFixed(2)}` },
                    { label: 'Weekly Balance', val: `${detail.customer.weekly_balance >= 0 ? '+' : ''}$${detail.customer.weekly_balance.toFixed(2)}`, color: detail.customer.weekly_balance >= 0 ? colors.odds : colors.negative },
                    { label: 'Available Credit', val: `$${detail.customer.available_credit.toFixed(2)}` },
                    { label: 'Pending', val: `$${detail.customer.pending_amount.toFixed(2)}`, color: colors.pending },
                    { label: 'Free Play', val: `$${detail.customer.free_play_balance.toFixed(2)}`, color: colors.freePlay },
                    { label: 'Last Login', val: detail.customer.last_login ? new Date(detail.customer.last_login).toLocaleDateString() : '—' },
                  ].map(({ label, val, color }) => (
                    <div key={label} style={{ background: colors.sectionBg, borderRadius: 6, padding: '0.6rem 0.75rem' }}>
                      <span style={detailLabel}>{label}</span>
                      <p style={{ ...detailVal, color: color || gold }}>{val}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Section tabs */}
              <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${colors.goldMuted}`, marginBottom: '1rem' }}>
                {[['bets', `Bets (${detail.bets.length})`], ['parlays', `Parlays (${detail.parlays.length})`], ['transactions', `Transactions (${detail.transactions.length})`], ['logins', 'Login IPs']].map(([id, label]) => (
                  <button key={id} onClick={() => setActiveSection(id)} style={{ padding: '0.6rem 1rem', background: 'none', border: 'none', cursor: 'pointer', color: activeSection === id ? gold : muted, borderBottom: activeSection === id ? `2px solid ${accent}` : '2px solid transparent', fontFamily, fontSize: '0.85rem', fontWeight: activeSection === id ? 700 : 400 }}>
                    {label}
                  </button>
                ))}
              </div>

              {/* Bets */}
              {activeSection === 'bets' && (
                <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${colors.goldMuted}`, background: colors.sectionBg }}>
                          {['Ticket', 'Description', 'Amount', 'Odds', 'Status', 'Date', 'Actions'].map(h => (
                            <th key={h} style={{ color: muted, padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 'normal' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {detail.bets.length === 0 ? (
                          <tr><td colSpan={7} style={{ padding: '1.5rem', textAlign: 'center', color: muted }}>No bets found</td></tr>
                        ) : detail.bets.map(bet => (
                          <tr key={bet.id} style={{ borderBottom: '1px solid rgba(191,149,63,0.08)' }}>
                            <td style={{ padding: '0.55rem 0.75rem', color: accent, fontFamily: 'monospace', fontSize: '0.78rem' }}>{bet.ticket_number}</td>
                            <td style={{ padding: '0.55rem 0.75rem', color: muted, maxWidth: 200 }}><span title={bet.description} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{bet.description}</span></td>
                            <td style={{ padding: '0.55rem 0.75rem', color: gold }}>${bet.amount.toFixed(2)}{bet.is_free_play && <span style={{ color: colors.freePlay, fontSize: '0.7rem', marginLeft: 4 }}>FP</span>}</td>
                            <td style={{ padding: '0.55rem 0.75rem', color: muted }}>{bet.odds}</td>
                            <td style={{ padding: '0.55rem 0.75rem' }}><StatusBadge status={bet.status} /></td>
                            <td style={{ padding: '0.55rem 0.75rem', color: muted, fontSize: '0.76rem' }}>{new Date(bet.created_at).toLocaleDateString()}</td>
                            <td style={{ padding: '0.55rem 0.75rem' }}>
                              {bet.status === 'pending' && (
                                <div style={{ display: 'flex', gap: 5 }}>
                                  <button onClick={() => { setShowSettleModal(bet.ticket_number); setSettleOutcome('won'); }} style={{ ...btnGreen, padding: '2px 8px', fontSize: '0.72rem' }}>Settle</button>
                                  <button onClick={() => voidTicket(bet.ticket_number)} style={{ ...btnDanger, padding: '2px 8px', fontSize: '0.72rem' }}>Void</button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Parlays */}
              {activeSection === 'parlays' && (
                <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${colors.goldMuted}`, background: colors.sectionBg }}>
                          {['Ticket', 'Legs', 'Amount', 'Odds', 'Status', 'Payout', 'Date', 'Actions'].map(h => (
                            <th key={h} style={{ color: muted, padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 'normal' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {detail.parlays.length === 0 ? (
                          <tr><td colSpan={8} style={{ padding: '1.5rem', textAlign: 'center', color: muted }}>No parlays found</td></tr>
                        ) : detail.parlays.map(p => (
                          <tr key={p.id} style={{ borderBottom: '1px solid rgba(191,149,63,0.08)' }}>
                            <td style={{ padding: '0.55rem 0.75rem', color: accent, fontFamily: 'monospace', fontSize: '0.78rem' }}>{p.ticket_number}</td>
                            <td style={{ padding: '0.55rem 0.75rem', color: colors.freePlay }}>{p.legs?.length || 0}L</td>
                            <td style={{ padding: '0.55rem 0.75rem', color: gold }}>${p.amount.toFixed(2)}</td>
                            <td style={{ padding: '0.55rem 0.75rem', color: muted }}>{p.combined_odds}</td>
                            <td style={{ padding: '0.55rem 0.75rem' }}><StatusBadge status={p.status} /></td>
                            <td style={{ padding: '0.55rem 0.75rem', color: colors.odds }}>{p.payout ? `$${p.payout.toFixed(2)}` : '—'}</td>
                            <td style={{ padding: '0.55rem 0.75rem', color: muted, fontSize: '0.76rem' }}>{new Date(p.created_at).toLocaleDateString()}</td>
                            <td style={{ padding: '0.55rem 0.75rem' }}>
                              {p.status === 'pending' && (
                                <div style={{ display: 'flex', gap: 5 }}>
                                  <button onClick={() => { setShowSettleModal(p.ticket_number); setSettleOutcome('won'); }} style={{ ...btnGreen, padding: '2px 8px', fontSize: '0.72rem' }}>Settle</button>
                                  <button onClick={() => voidTicket(p.ticket_number)} style={{ ...btnDanger, padding: '2px 8px', fontSize: '0.72rem' }}>Void</button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Transactions */}
              {activeSection === 'transactions' && (
                <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${colors.goldMuted}`, background: colors.sectionBg }}>
                          {['Type', 'Amount', 'Description', 'Date'].map(h => (
                            <th key={h} style={{ color: muted, padding: '0.6rem 0.75rem', textAlign: 'left', fontWeight: 'normal' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {detail.transactions.length === 0 ? (
                          <tr><td colSpan={4} style={{ padding: '1.5rem', textAlign: 'center', color: muted }}>No transactions</td></tr>
                        ) : detail.transactions.map(t => (
                          <tr key={t.id} style={{ borderBottom: '1px solid rgba(191,149,63,0.08)' }}>
                            <td style={{ padding: '0.55rem 0.75rem', color: accent, fontSize: '0.78rem' }}>{t.type}</td>
                            <td style={{ padding: '0.55rem 0.75rem', color: t.amount >= 0 ? colors.odds : colors.negative, fontWeight: 600 }}>{t.amount >= 0 ? '+' : ''}${t.amount.toFixed(2)}</td>
                            <td style={{ padding: '0.55rem 0.75rem', color: muted }}>{t.description}</td>
                            <td style={{ padding: '0.55rem 0.75rem', color: muted, fontSize: '0.76rem' }}>{new Date(t.created_at).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Login IPs */}
              {activeSection === 'logins' && (
                <div style={card}>
                  {(!detail.login_history || detail.login_history.length === 0) ? (
                    <p style={{ color: muted, textAlign: 'center', padding: '1.5rem 0', margin: 0 }}>No login history recorded</p>
                  ) : (
                    <>
                      <h4 style={{ color: gold, margin: '0 0 0.75rem 0', fontSize: '0.88rem' }}>IP Address Summary</h4>
                      <div style={{ overflowX: 'auto', marginBottom: '1.25rem' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                          <thead>
                            <tr style={{ borderBottom: `1px solid ${colors.goldMuted}`, background: colors.sectionBg }}>
                              {['IP Address', 'Logins', 'First Seen', 'Last Seen'].map(h => (
                                <th key={h} style={{ color: muted, padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 'normal' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {detail.login_history.map((ip, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid rgba(191,149,63,0.08)' }}>
                                <td style={{ padding: '0.5rem 0.75rem', color: gold, fontFamily: 'monospace' }}>{ip.ip_address}</td>
                                <td style={{ padding: '0.5rem 0.75rem', color: colors.freePlay }}>{ip.login_count}</td>
                                <td style={{ padding: '0.5rem 0.75rem', color: muted, fontSize: '0.77rem' }}>{new Date(ip.first_login).toLocaleDateString()}</td>
                                <td style={{ padding: '0.5rem 0.75rem', color: muted, fontSize: '0.77rem' }}>{new Date(ip.last_login).toLocaleDateString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <h4 style={{ color: gold, margin: '0 0 0.75rem 0', fontSize: '0.88rem' }}>Recent Logins</h4>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                          <thead>
                            <tr style={{ borderBottom: `1px solid ${colors.goldMuted}`, background: colors.sectionBg }}>
                              {['IP Address', 'Date & Time'].map(h => (
                                <th key={h} style={{ color: muted, padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 'normal' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {detail.recent_logins.map((l, i) => (
                              <tr key={i} style={{ borderBottom: '1px solid rgba(191,149,63,0.08)' }}>
                                <td style={{ padding: '0.5rem 0.75rem', color: gold, fontFamily: 'monospace' }}>{l.ip_address}</td>
                                <td style={{ padding: '0.5rem 0.75rem', color: muted, fontSize: '0.77rem' }}>{new Date(l.logged_in_at).toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* Settle Modal */}
      {showSettleModal && (
        <div style={modalOverlay}>
          <div style={{ ...modalBox, maxWidth: 380 }}>
            <h3 style={{ color: gold, margin: '0 0 0.75rem 0' }}>Settle Ticket</h3>
            <p style={{ color: muted, fontSize: '0.85rem', marginBottom: '1rem' }}>Ticket: <strong style={{ color: gold }}>{showSettleModal}</strong></p>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem' }}>
              {['won', 'lost'].map(o => (
                <label key={o} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: gold, cursor: 'pointer' }}>
                  <input type="radio" value={o} checked={settleOutcome === o} onChange={() => setSettleOutcome(o)} style={{ accentColor: accent }} />
                  {o.toUpperCase()}
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowSettleModal(null)} style={btnCancel}>Cancel</button>
              <button onClick={settleTicket} style={settleOutcome === 'won' ? btnGreen : btnDanger}>Settle as {settleOutcome}</button>
            </div>
          </div>
        </div>
      )}

      {/* Adjust Balance Modal */}
      {showAdjustModal && (
        <div style={modalOverlay}>
          <div style={{ ...modalBox, maxWidth: 400 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h3 style={{ color: gold, margin: 0 }}>Adjust Balance</h3>
              <button onClick={() => setShowAdjustModal(false)} style={{ background: 'none', border: 'none', color: muted, cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
              <div>
                <label style={{ display: 'block', color: muted, fontSize: '0.82rem', marginBottom: '0.3rem' }}>Type</label>
                <select value={adjustType} onChange={e => setAdjustType(e.target.value)} style={{ ...inputStyle, width: '100%', background: selectSurface }}>
                  <option style={optionStyle} value="credit">Credit Adjustment</option>
                  <option style={optionStyle} value="free_play">Free Play</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', color: muted, fontSize: '0.82rem', marginBottom: '0.3rem' }}>Amount (use negative to deduct)</label>
                <input type="number" step="0.01" value={adjustAmount} onChange={e => setAdjustAmount(e.target.value)} placeholder="e.g. 50.00 or -25.00" style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', color: muted, fontSize: '0.82rem', marginBottom: '0.3rem' }}>Reason</label>
                <input type="text" value={adjustReason} onChange={e => setAdjustReason(e.target.value)} placeholder="Reason for adjustment..." style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button onClick={() => setShowAdjustModal(false)} style={btnCancel}>Cancel</button>
                <button onClick={submitAdjust} style={btnPrimary}>Apply</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Live Fraud View (full access for CS staff) ───────────────────────────────
function CSFraudView() {
  const [flagged, setFlagged] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [incidentsLoading, setIncidentsLoading] = useState(false);
  const [actionInput, setActionInput] = useState('');
  const [filter, setFilter] = useState('unreviewed');

  useEffect(() => { loadFlagged(); }, []);

  const loadFlagged = async () => {
    try {
      setLoading(true);
      const data = await api.admin.getFraudFlagged();
      setFlagged(data.flagged || []);
    } catch {}
    finally { setLoading(false); }
  };

  const openIncidents = async (flag) => {
    setSelected(flag);
    setIncidents([]);
    setActionInput('');
    setIncidentsLoading(true);
    try {
      const data = await api.admin.getFraudIncidents(flag.user_id);
      setIncidents(data.incidents || []);
    } catch {}
    finally { setIncidentsLoading(false); }
  };

  const submitReview = async (flagId) => {
    try {
      await api.admin.reviewFraudFlag(flagId, actionInput);
      setSelected(s => s ? { ...s, reviewed: true, action_taken: actionInput } : s);
      loadFlagged();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to submit review');
    }
  };

  const filtered = flagged.filter(f => {
    if (filter === 'unreviewed') return !f.reviewed;
    if (filter === 'reviewed') return f.reviewed;
    return true;
  });

  const unreviewedCount = flagged.filter(f => !f.reviewed).length;

  const filterBtn = (val, label) => (
    <button
      onClick={() => setFilter(val)}
      style={{ ...btnPrimary, background: filter === val ? colors.goldActiveBg : 'transparent', opacity: filter === val ? 1 : 0.6 }}
    >
      {label}
    </button>
  );

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <h3 style={{ color: gold, margin: 0, fontSize: '1rem' }}>Live Odds Fraud Flags</h3>
        <button onClick={loadFlagged} style={{ ...btnPrimary, padding: '0.3rem 0.7rem', fontSize: '0.78rem' }}>Refresh</button>
      </div>
      <p style={{ color: muted, fontSize: '0.82rem', margin: '0 0 1rem 0' }}>
        Accounts flagged for placing bets just before significant odds drops. Review and record actions taken.
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {filterBtn('unreviewed', `Unreviewed (${unreviewedCount})`)}
        {filterBtn('reviewed', `Reviewed (${flagged.length - unreviewedCount})`)}
        {filterBtn('all', `All (${flagged.length})`)}
      </div>

      {loading && <p style={{ color: muted }}>Loading…</p>}
      {!loading && filtered.length === 0 && (
        <p style={{ color: muted, fontSize: '0.9rem' }}>
          {filter === 'unreviewed' ? 'No unreviewed flags — all clear.' : 'No flags in this category.'}
        </p>
      )}
      {!loading && filtered.length > 0 && (
        <div style={{ overflowX: 'auto', marginBottom: '1.25rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.goldMuted}` }}>
                {['User', 'Incidents (7d)', 'Flagged At', 'Status', 'Action Taken', 'Actions'].map(h => (
                  <th key={h} style={{ color: muted, padding: '0.45rem 0.6rem', textAlign: 'left', fontWeight: 'normal' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(flag => (
                <tr key={flag.id} style={{ borderBottom: '1px solid rgba(191,149,63,0.08)', background: selected?.id === flag.id ? 'rgba(191,149,63,0.06)' : 'transparent' }}>
                  <td style={{ padding: '0.45rem 0.6rem' }}>
                    <div style={{ color: gold, fontWeight: 600 }}>{flag.user_name}</div>
                    <div style={{ color: muted, fontSize: '0.75rem' }}>@{flag.user_username}</div>
                  </td>
                  <td style={{ padding: '0.45rem 0.6rem', color: colors.negative, fontWeight: 700 }}>{flag.incident_count}</td>
                  <td style={{ padding: '0.45rem 0.6rem', color: muted, fontSize: '0.77rem' }}>
                    {flag.flagged_at ? new Date(flag.flagged_at).toLocaleString() : '—'}
                  </td>
                  <td style={{ padding: '0.45rem 0.6rem' }}>
                    <span style={{ fontSize: '0.74rem', padding: '2px 7px', borderRadius: 20, background: flag.reviewed ? `${colors.odds}26` : `${colors.negative}26`, color: flag.reviewed ? colors.odds : colors.negative }}>
                      {flag.reviewed ? 'Reviewed' : 'Pending'}
                    </span>
                  </td>
                  <td style={{ padding: '0.45rem 0.6rem', color: muted, fontSize: '0.77rem' }}>{flag.action_taken || '—'}</td>
                  <td style={{ padding: '0.45rem 0.6rem' }}>
                    <button onClick={() => openIncidents(flag)} style={{ ...btnPrimary, padding: '0.25rem 0.65rem', fontSize: '0.77rem' }}>View Incidents</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div style={{ background: colors.sectionBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 6, padding: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <div>
              <span style={{ color: gold, fontWeight: 600, fontSize: '0.95rem' }}>{selected.user_name}</span>
              <span style={{ color: muted, fontSize: '0.8rem', marginLeft: 8 }}>@{selected.user_username} · {selected.incident_count} incidents</span>
            </div>
            <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: muted, cursor: 'pointer', fontSize: '1.2rem' }}>×</button>
          </div>

          {incidentsLoading ? (
            <p style={{ color: muted, fontSize: '0.85rem' }}>Loading incidents…</p>
          ) : incidents.length === 0 ? (
            <p style={{ color: muted, fontSize: '0.85rem' }}>No incidents found.</p>
          ) : (
            <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(191,149,63,0.15)' }}>
                    {['Placed At', 'Selection', 'Market', 'Bet Odds', 'Post-Change', 'Advantage', 'Secs Before', 'Amount'].map(h => (
                      <th key={h} style={{ color: muted, padding: '0.35rem 0.5rem', textAlign: 'left', fontWeight: 'normal', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {incidents.map(inc => (
                    <tr key={inc.id} style={{ borderBottom: '1px solid rgba(191,149,63,0.06)' }}>
                      <td style={{ padding: '0.35rem 0.5rem', color: muted, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                        {inc.bet_placed_at ? new Date(inc.bet_placed_at).toLocaleString() : '—'}
                      </td>
                      <td style={{ padding: '0.35rem 0.5rem', color: gold }}>{inc.selection || '—'}</td>
                      <td style={{ padding: '0.35rem 0.5rem', color: muted }}>{inc.market_type || '—'}</td>
                      <td style={{ padding: '0.35rem 0.5rem', color: gold, fontFamily: 'monospace' }}>{inc.bet_odds?.toFixed(3)}</td>
                      <td style={{ padding: '0.35rem 0.5rem', color: colors.negative, fontFamily: 'monospace' }}>{inc.odds_after_change?.toFixed(3)}</td>
                      <td style={{ padding: '0.35rem 0.5rem', color: colors.odds, fontFamily: 'monospace' }}>+{inc.odds_advantage?.toFixed(3)}</td>
                      <td style={{ padding: '0.35rem 0.5rem', color: colors.pending, fontFamily: 'monospace' }}>{inc.seconds_before_change?.toFixed(1)}s</td>
                      <td style={{ padding: '0.35rem 0.5rem', color: gold }}>{inc.bet_amount != null ? `${inc.bet_amount.toFixed(2)}₵₵` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!selected.reviewed ? (
            <div style={{ borderTop: `1px solid ${colors.goldMuted}`, paddingTop: '0.85rem', display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', color: muted, fontSize: '0.8rem', marginBottom: '0.3rem' }}>
                  Action taken (e.g. "Account suspended", "Warning issued", "No action — false positive")
                </label>
                <input
                  type="text"
                  value={actionInput}
                  onChange={e => setActionInput(e.target.value)}
                  placeholder="Describe action taken…"
                  style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                />
              </div>
              <button onClick={() => submitReview(selected.id)} style={btnDanger}>Mark Reviewed</button>
            </div>
          ) : (
            <div style={{ borderTop: `1px solid ${colors.goldMuted}`, paddingTop: '0.75rem' }}>
              <span style={{ color: colors.odds, fontSize: '0.85rem' }}>
                ✓ Reviewed by {selected.reviewed_by || 'staff'} — {selected.action_taken || 'no action recorded'}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Rules & FAQ View (read-only for CS staff) ───────────────────────────────
function CSRulesView() {
  const [rules, setRules] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState('rules');

  useEffect(() => {
    api.getRules()
      .then(data => { setRules(data.rules || []); setFaqs(data.faqs || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const items = section === 'rules' ? rules : faqs;
  const label = section === 'rules' ? 'House Rules' : 'Frequently Asked Questions';

  return (
    <div style={card}>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
        {['rules', 'faq'].map(s => (
          <button
            key={s}
            onClick={() => setSection(s)}
            style={{ ...btnPrimary, background: section === s ? colors.goldActiveBg : 'transparent', opacity: section === s ? 1 : 0.6 }}
          >
            {s === 'rules' ? 'House Rules' : 'FAQ'}
          </button>
        ))}
      </div>
      <h3 style={{ color: gold, margin: '0 0 1rem 0', fontSize: '1rem' }}>{label}</h3>
      {loading && <p style={{ color: muted }}>Loading…</p>}
      {!loading && items.length === 0 && <p style={{ color: muted, fontSize: '0.9rem' }}>No entries published yet.</p>}
      {!loading && items.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
          {items.map((item, i) => (
            <div key={item.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
              <span style={{ color: accent, fontWeight: 700, minWidth: 24, flexShrink: 0 }}>{i + 1}.</span>
              <span style={{ color: gold, fontSize: '0.9rem', lineHeight: 1.6 }}>{item.content}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
